import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Drawer,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  styled,
  tableCellClasses
} from '@mui/material'
import Pagination from '../../../../components/TablePagination'
import React, { useEffect, useRef } from 'react'
import { Button as GenericButton } from '../../../../components/Button'
import { type ConnectedProps, connect, useSelector } from 'react-redux'
import { type RootState } from '../../../../store'
import { visuallyHidden } from '@mui/utils'
import AuthError from '../../../../components/ErrorComponents/AuthError'
import { type HybridKEK } from '../../types'
import { type HybridKekListState } from '../../redux/hybrid/HybridKekListSlice'
import { type getUsersParameterState } from '../../redux/hybrid/getAllUsersFilterParameterSlice'
import LanguageStrings from '../../../../i18n/locales'
import {
  fetchHybridKEKList,
  getAllUsersFilterParameterInfo,
  getUsersInfo
} from '../../redux/actionCreator'
import SearchBox from '../../../../components/SearchBox'
import CloseIcon from '@mui/icons-material/Close'
import ReplayIcon from '@mui/icons-material/Replay'
import FilterIcon from '../../../../assets/filterIcon.png'
import { GenXDropdown, type Option } from '../../../../components/GenXDropdown'

const HSMConfigurationRegularKekStrings = LanguageStrings().HSMConfiguration.RegularKek
const HSMConfigurationHybridKekStrings = LanguageStrings().HSMConfiguration.HybridKek

type Order = 'asc' | 'desc'

interface Sort {
  order: Order
  orderBy: string
}

const style = {
  width: '99%',
  bgcolor: '#313030',
  border: '1px solid #101010',
  borderRadius: '10px',
  boxShadow: '1px 1px 1px rgba(0, 0, 0, 0.15)',
  marginBottom: '16px',
  padding: 0
}

interface EnhancedTableProps {
  numSelected: number
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void
  rowCount: number
  dataList: HybridKEK[]
  order: Order
  orderBy: string
  onRequestSort: (event: React.MouseEvent<unknown>, newOrderBy: keyof HybridKEK) => void
}

function EnhancedTableHead (props: EnhancedTableProps): any {
  const { onSelectAllClick, order, orderBy, onRequestSort, numSelected, rowCount, dataList } = props
  const createSortHandler =
    (newOrderBy: keyof HybridKEK) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, newOrderBy)
    }
  return (dataList !== null && dataList !== undefined && <>
    <TableHead data-testid='table-header'>
      <TableRow>
        <StyledTableCell padding="checkbox">
          <Checkbox
            sx={{ color: '#909090' }}
            data-testid="device-table-header-checkbox"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{
              'aria-label': 'Select all Assets',
              id: 'select-all-items'
            }}
          />
        </StyledTableCell>
          {headCells.map((headCell: HeadCell) => (
            <StyledTableCell
              key={headCell.id}
              align='left'
              padding={headCell.disablePadding ? 'none' : 'normal'}
              sortDirection={orderBy === headCell.id ? order : false}
            >
              <TableSortLabel
                hideSortIcon={!headCell.sortable}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={headCell.sortable ? createSortHandler(headCell.id) : undefined}
                aria-label={headCell.label}
                data-testid="test-sort"
                sx={{ cursor: headCell.sortable ? 'pointer' : 'text' }}
              >
                {headCell.id}
                {orderBy === headCell.id
                  ? (
                    // istanbul ignore next
                    <Box component="span" sx={visuallyHidden}>
                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                    )
                  : null}
              </TableSortLabel>
            </StyledTableCell>
          ))}
      </TableRow>
    </TableHead>
  </>
  )
}

interface HeadCell {
  disablePadding: boolean
  id: any
  label: string
  numeric: boolean
  sortable: boolean
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: '#F0F0F0',
    fontWeight: '700',
    fontSize: 12,
    border: '2px solid #202020',
    padding: '5px 16px',
    fontFamily: 'Honeywell Sans Web',
    lineHeight: '20px'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: '#d0d0d0',
    paddingLeft: '17px',
    borderBottom: '1px solid #202020',
    borderRight: 0,
    fontFamily: 'Honeywell Sans Web',
    whiteSpace: 'nowrap'
  }
}))

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '& th': {
    border: '1px solid #202020'
  }
}))

const headCells: readonly HeadCell[] = [
  {
    id: 'label',
    numeric: false,
    disablePadding: false,
    label: HSMConfigurationRegularKekStrings.Label,
    sortable: true
  },
  {
    id: 'tenant',
    numeric: false,
    disablePadding: false,
    label: HSMConfigurationRegularKekStrings.Tenant,
    sortable: true
  },
  {
    id: 'regular Wrapping Key',
    numeric: false,
    disablePadding: false,
    label: HSMConfigurationHybridKekStrings.RegularWrappingKeyLabel,
    sortable: true
  },
  {
    id: 'created At',
    numeric: false,
    disablePadding: false,
    label: HSMConfigurationRegularKekStrings.CreatedAt,
    sortable: true
  },
  {
    id: 'created By',
    numeric: false,
    disablePadding: false,
    label: HSMConfigurationRegularKekStrings.CreatedBy,
    sortable: true
  }
]

interface FilterState {
  user: Option | null
}

const HybridKekTableComponent = (props: PropsFromRedux): JSX.Element => {
  const [hybridKekList, setHybridKekList] = React.useState<HybridKEK[]>([])
  const [selected, setSelected] = React.useState<string[]>([])
  const [checkedKek, setCheckedHybridKekList] = React.useState<HybridKEK[]>([])
  const [page, setPage] = React.useState(1)
  const [sort, setSort] = React.useState<Sort>({ order: 'desc', orderBy: 'created At' })
  const [searchValue, setSearchValue] = React.useState<string | null>('')
  const rowsPerPage = 10
  const numAssignedAssets = useSelector((state: RootState) => { return state.hybridKekList.hybridKekList?.total_count })

  const [openFilter, setOpenFilter] = React.useState(false)
  const [showFilter, setShowFilter] = React.useState(false)
  const [userOptions, setUserOptions] = React.useState<Option[]>([])
  const [filterState, setFilterState] = React.useState<FilterState>({ user: null })
  const [appliedFilters, setAppliedFilters] = React.useState<FilterState>({ user: null })
  const UsersFilterParameter = useSelector((state: RootState) => state.getUsersSlice)

  React.useEffect(() => {
    const userData = UsersFilterParameter.data

    if (userData !== null && Array.isArray(userData.data) && userData.data.length > 0) {
      const usersOptions: Option[] = []

      userData.data.forEach((item: any) => {
        usersOptions.push({
          value: item,
          label: item
        })
      })

      setUserOptions(usersOptions)
    }
  }, [UsersFilterParameter])

  const calledOnce = useRef(false)
  useEffect(() => {
    if (!calledOnce.current) {
      props.getUsersInfo()
      handleFetchHybridKEKList()
      calledOnce.current = true
    }
  }, [])

  const handleUserSelect = (selectedValue: Option): void => {
    setFilterState({ ...filterState, user: selectedValue })
  }

  const resetFilterState = (): void => {
    setFilterState(appliedFilters)
    setOpenFilter(false)
  }

  const resetAppliedFilters = (): void => {
    setAppliedFilters({ user: null })
  }

  const showFilterDetails = (pageNum: number): void => {
    props.getAllUsersFilterParameterInfo(
      pageNum,
      rowsPerPage,
      sort.order,
      sort.orderBy,
      searchValue ?? '',
      {
        createdBy: filterState.user === null ? [] : [filterState.user.value]
      })
  }

  const filtersUpdated = (): boolean => {
    return (filterState.user !== appliedFilters.user)
  }

  const handleRowClick = (id: string): void => {
    const isIdSelected = selected.includes(id)
    if (!isIdSelected) {
      setCheckedHybridKekList([...checkedKek, hybridKekList.filter((row: HybridKEK) => row.id === id)[0]])
      setSelected([...selected, id])
    } else {
      const temp = [...checkedKek].filter((row: HybridKEK) => row.id !== id)
      setCheckedHybridKekList(temp)
      setSelected(selected.filter((selectedId) => selectedId !== id))
    }
  }

  const handleFetchHybridKEKList = (): void => {
    if (page >= 1) {
      props.fetchHybridKEKList(
        page - 1,
        rowsPerPage,
        sort.order,
        sort.orderBy,
        searchValue ?? ''
      )
    }
  }

  React.useEffect(() => {
    if (appliedFilters.user === null) {
      if (props.hybridKekListState.hybridKekList !== null && props.hybridKekListState.httpStatus === 200 && (props.hybridKekListState.hybridKekList.total_count / rowsPerPage) >= page - 1) {
        handleFetchHybridKEKList()
      } else { setPage(1) }
    } else {
      if (props.hybridKekListState.hybridKekList !== null && props.hybridKekListState.httpStatus === 200 && (props.hybridKekListState.hybridKekList.total_count / rowsPerPage) >= page - 1) {
        showFilterDetails(page - 1)
      } else {
        setPage(1)
      }
    }
  }, [appliedFilters, page, sort])

  const handleSearch = (): void => {
    if (page >= 1) {
      setPage(1)
      setFilterState({ user: null })
      setAppliedFilters({ user: null })
    }
  }

  const handleRequestSort = (event: React.MouseEvent<unknown>, newOrderBy: keyof HybridKEK): void => {
    const isAsc = sort.orderBy === newOrderBy && sort.order === 'asc'
    const toggledOrder = isAsc ? 'desc' : 'asc'
    setSort({ order: toggledOrder, orderBy: newOrderBy })
  }

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const currentPageKekList = hybridKekList.map((hybridKEK: HybridKEK) => hybridKEK.id)
    if (event.target.checked) {
      const newSelected: string[] = []
      const newSelectedKeks: HybridKEK[] = []
      currentPageKekList.forEach((id) => {
        if (!selected.includes(id)) {
          newSelected.push(id)
        }
      })
      hybridKekList.forEach((row: HybridKEK) => {
        if (newSelected.includes(row.id)) {
          newSelectedKeks.push(row)
        }
      })
      setSelected([...selected, ...newSelected])
      setCheckedHybridKekList([...checkedKek, ...newSelectedKeks])
    } else {
      const newSelected: string[] = []
      selected.forEach((id) => {
        if (!currentPageKekList.includes(id)) {
          newSelected.push(id)
        }
      })
      const newSelectedKeks: HybridKEK[] = [...checkedKek].filter((row: HybridKEK) => !currentPageKekList.includes(row.id))
      setSelected(newSelected)
      setCheckedHybridKekList(newSelectedKeks)
    }
  }

  const isSelected = (id: string): boolean => selected.includes(id)

  const getDisplayKekList = (): { list: HybridKEK[], total_count: number } => {
    return {
      list: props.hybridKekListState.hybridKekList?.list ?? [],
      total_count: props.hybridKekListState.hybridKekList?.total_count ?? 0
    }
  }

  const getDisplayHttpStatus = (): number | null => {
    return props.hybridKekListState.httpStatus
  }

  const getIsLoading = (): boolean => {
    return props.hybridKekListState.isLoading
  }

  const getError = (): string | null => {
    return props.hybridKekListState.error
  }

  useEffect(() => {
    setHybridKekList(getDisplayKekList().list)
  }, [props.hybridKekListState, appliedFilters])

  const renderTable = (kekList: any[], totalCount: number): JSX.Element => (
    <>
      <Paper sx={{
        width: '100%',
        mb: 2,
        backgroundColor: '#272727',
        boxShadow: 'unset',
        color: '#F0F0F0',
        marginBottom: '0'
      }}>
        <TableContainer className="device-table">
          <Table sx={{ minWidth: 750, color: '#f0f0f0' }} aria-labelledby="tableTitle" size={'small'}>
            <EnhancedTableHead
              numSelected={selected.filter((label) => kekList.map((kek: HybridKEK) => kek.id).includes(label)).length}
              order={sort.order}
              orderBy={sort.orderBy}
              onRequestSort={handleRequestSort}
              onSelectAllClick={handleSelectAllClick}
              rowCount={kekList.length}
              dataList={kekList}
            />
            <TableBody>
              {kekList.map((row: HybridKEK, index: number) => {
                const isItemSelected = isSelected(row.id)
                const labelId = `enhanced-table-checkbox-${row.id}`
                return (
                  <StyledTableRow
                    hover
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row.id}
                    aria-label="groups-list-row"
                    selected={isItemSelected}
                  >
                    <StyledTableCell padding="checkbox">
                      <Checkbox
                        data-testid={`checkbox-${index}`}
                        sx={{ color: '#909090' }}
                        checked={isItemSelected}
                        onChange={() => {
                          handleRowClick(row.id)
                        }}
                        inputProps={{
                          'aria-labelledby': labelId,
                          'aria-label': 'test-checkbox',
                          id: `select-item-${index}`
                        }}
                      />
                    </StyledTableCell>
                    <StyledTableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      padding="none"
                      aria-label="group-name"
                      data-testid="group-name"
                      sx={{
                        color: '#64C3FF !important',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '15vw',
                        cursor: 'pointer'
                      }}
                    >
                      {(row.label != null) && row.label.length > 12 ? <abbr style={{ textDecoration: 'none' }} title={row.label}>{row.label}</abbr> : row.label}
                    </StyledTableCell>
                    <StyledTableCell>{row.tenantId}</StyledTableCell>
                    <StyledTableCell>{row.wrapperId}</StyledTableCell>
                    <StyledTableCell>{(row.createdAt != null) ? new Date(row.createdAt).toLocaleString() : ''}</StyledTableCell>
                    <StyledTableCell
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '15vw',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {(row.createdBy != null) && row.createdBy.length > 12 ? <abbr style={{ textDecoration: 'none' }} title={row.createdBy}>{row.createdBy}</abbr> : row.createdBy}
                    </StyledTableCell>
                  </StyledTableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {totalCount > 10 && (
          <Pagination
            count={totalCount}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            siblingCount={1}
            boundaryCount={1}
          />
        )}
      </Paper>
      {selected.length === 0
        ? /* istanbul ignore next */ null
        : (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#272727',
            borderTop: '2px solid #202020'
          }}>
            <GenericButton type='borderless' dataTestId='cancel-btn' onClick={() => {
              setSelected([])
            }}>
              {HSMConfigurationRegularKekStrings.Cancel}
            </GenericButton>
          </div>
          )}
    </>
  )

  const handleTable = (): JSX.Element => {
    const displayKekList = getDisplayKekList()
    const displayHttpStatus = getDisplayHttpStatus()
    const isLoading = getIsLoading()
    const error = getError()

    if (isLoading) {
      return <div className='CircularprogressAmi'><CircularProgress/></div>
    }

    if (error != null) {
      return <div className='authError'><AuthError errorMessage={error}/></div>
    }

    if (displayKekList.total_count > 0 && displayHttpStatus === 200) {
      return renderTable(displayKekList.list, displayKekList.total_count)
    }

    if (displayHttpStatus === 401) {
      return <div className='authError'><AuthError errorMessage="Unauthorized"/></div>
    }
    if (displayHttpStatus === 403) {
      return <div className='authError'><AuthError errorMessage="accessForbidden"/></div>
    }
    if (displayHttpStatus === 404) {
      return <div className='authError'><AuthError errorMessage="NoDataPresent"/></div>
    }
    if (displayHttpStatus === 400) {
      return <div className="authError"><AuthError errorMessage="invalidSearchInput"/></div>
    }

    if (displayHttpStatus === 200 && displayKekList.total_count === 0) {
      return <div className='authError'><AuthError errorMessage="NoDataPresent"/></div>
    }

    if (displayHttpStatus === 500) {
      return <div className='authError'><AuthError errorMessage="cannotFetch" /></div>
    }

    if ((appliedFilters != null) && displayHttpStatus === 500) {
      return <div className='authError'><AuthError errorMessage="cannotFetch" retry={() => { showFilterDetails(page - 1) }} /></div>
    }
    return <div className='authError'><AuthError errorMessage="cannotFetch" retry={() => { handleFetchHybridKEKList() }} /></div>
  }

  return (
    <>
      <Box sx={style} className="devices">
        <div className='searchSection' style={{ width: '100%', backgroundColor: '#272727' }}>
          <Paper className='searchBoxComponent' sx={{ p: '2px 4px', display: 'flex', alignItems: 'center' }} >
            {/* Filter Button */}
            <Button data-testid='filter-data' className="filter-btn"
              sx={{ background: '#707070', color: '#D0D0D0' }} onClick={() => {
                setOpenFilter(true)
              }}
            >
              <img src={FilterIcon} height={16} width={16} style={{ marginLeft: '7px' }}/>{HSMConfigurationRegularKekStrings.Filter}
            </Button>
            <SearchBox immediate={true} debounce={true} placeholder={HSMConfigurationRegularKekStrings.SearchUsingTenantNameCategoryCreatedBy}
              sx={{ width: '450px' }}
              emptyEnterFallback={() => {
                if (page >= 1) {
                  props.fetchHybridKEKList(page - 1, rowsPerPage, sort.order, sort.orderBy, '')
                }
              }} searchVal={searchValue} setSearchVal={setSearchValue}
              performAction={() => {
                handleSearch()
              }}/>
          </Paper>
        </div>
        <Drawer
          anchor="right"
          open={openFilter}
          className='filter-drawer'
          variant='temporary'
          onClose={() => {
            setOpenFilter(false)
            resetFilterState()
          }}
        >
          <div className='filter-headar'>
            <div className='title' style={{ marginTop: '15px' }}>{HSMConfigurationRegularKekStrings.Filter}</div>
            <div>
              <Button aria-label='clear-all-filter' className='cancel' style={{ marginTop: '-2px', marginLeft: '80px' }} onClick={() => {
                setFilterState({ user: null })
              }}>
                <ReplayIcon sx={{
                  width: '16px',
                  height: '16px',
                  marginRight: '8px'
                }}/>{HSMConfigurationRegularKekStrings.ClearAll}</Button>
            </div>
            <div>
              <CloseIcon aria-label='close-drawer' sx={{ color: '#f0f0f0', cursor: 'pointer' }}
                onClick={() => {
                  setOpenFilter(false)
                  resetFilterState()
                }}/>
            </div>
          </div>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
            <Grid item xs={12}>
              <div className='type-name' style={{ marginTop: '40px' }}>{HSMConfigurationHybridKekStrings.CreatedBy}</div>
              {/* Filter Dropdown */}
              <GenXDropdown placeholder={HSMConfigurationHybridKekStrings.SelectUser}
                value={filterState.user} options={userOptions} onSelect={handleUserSelect}/>
            </Grid>
          </Grid>
          <div className='divider'/>
          <div className='buttons-container'>
            <Button className='cancel' aria-label='cancel-drawer' onClick={() => {
              resetFilterState()
              setOpenFilter(false)
            }}>{HSMConfigurationRegularKekStrings.Cancel}</Button>
            <GenericButton aria-label='confirm-filter' data-testid='show-results'
              primaryColor={!filtersUpdated() ? '#B0B0B0' : undefined}
              disabled={!filtersUpdated()}
              onClick={() => {
                setAppliedFilters(filterState)
                setShowFilter(true)
                setOpenFilter(false)
                setPage(1)
                showFilterDetails(0)
                setSearchValue(null)
              }}>{HSMConfigurationRegularKekStrings.ShowDetails}</GenericButton>
          </div>
        </Drawer>
        {showFilter &&
          <div
            style={{
              padding: '12px 24px',
              borderTop: '2px solid #202020',
              color: '#d0d0d0'
            }}
          >
            {appliedFilters.user === null
              ? (
                  HSMConfigurationRegularKekStrings.NoFiltersApplied
                )
              : (
                <div className="classNAme">
                  {numAssignedAssets} {HSMConfigurationRegularKekStrings.ResultsOrFilters}
                  <div className="box">
                    {HSMConfigurationHybridKekStrings.CreatedBy}: {appliedFilters.user.label}
                  </div>
                  <em
                    onClick={() => {
                      resetAppliedFilters()
                      setFilterState({ user: null })
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {HSMConfigurationRegularKekStrings.ClearFilters}{' '}
                  </em>
                </div>
                )}
          </div>
        }
        {handleTable()}
      </Box>
    </>
  )
}

interface DispatchToProps {
  fetchHybridKEKList: (page: number, size: number, sortdir: string, sortfield: string, input: string) => void
  getAllUsersFilterParameterInfo: (page: number, size: number, sortdir: string, sortfield: string, input: string, filterParams: any) => void
  getUsersInfo: () => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  getUsersInfo: () => dispatch(getUsersInfo()),
  getAllUsersFilterParameterInfo: (page: number, size: number, sortdir: string, sortfield: string, input: string, filterParams: any) => dispatch(getAllUsersFilterParameterInfo(page, size, sortdir, sortfield, input, filterParams)),
  fetchHybridKEKList: (page: number, size: number, sortdir: string, sortfield: string, input: string) => dispatch(fetchHybridKEKList(page, size, sortdir, sortfield, input))
})

interface StateToProps {
  hybridKekListState: HybridKekListState
  hybridKekFilterState: getUsersParameterState
}

const mapStateToProps = (state: RootState): StateToProps => ({
  hybridKekListState: state.hybridKekList,
  hybridKekFilterState: state.getUsersSlice
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>
export default connector(HybridKekTableComponent)
